<template>
  <span>
    <b-card style="max-height: 800px; overflow-y: auto">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h4>Activity Log for Site List</h4>
        <b-button variant="outline-primary" size="sm" @click="$router.go(-1)">
          Back
        </b-button>
      </div>
      <app-timeline>
        <template v-for="row in rows">
          <!-- Display created items -->
          <app-timeline-item :key="row.id" v-if="row.event === 'created'">
            <div
              class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
            >
              <h6
                v-html="formatMsg(row.log_name, row.properties, row.event)"
              ></h6>
              <small class="text-muted"
                >Created: {{ formatDate(row.created_at) }}</small
              >
            </div>
          </app-timeline-item>

          <!-- Display updated items with old and new values -->
          <app-timeline-item
            :key="row.id + '-updated'"
            v-if="
              row.event === 'updated' &&
              Object.keys(getFilteredChanges(row.properties.changes)).length > 0
            "
            variant="warning"
          >
            <div
              class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
            >
              <h6
                v-html="formatMsg(row.log_name, row.properties, row.event)"
              ></h6>
              <small class="text-muted"
                >Updated: {{ formatDate(row.created_at) }}</small
              >
            </div>

            <b-button
              v-b-toggle="'report-list-' + row.id"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              size="sm"
              variant="outline-primary"
            >
              Show Changes
            </b-button>
            <b-collapse :id="'report-list-' + row.id">
              <b-list-group flush class="mt-1">
                <b-list-group-item
                  class="d-flex justify-content-between align-items-center bg-transparent"
                >
                  <ul>
                    <li
                      v-for="(oldValue, key) in getFilteredChanges(
                        row.properties.changes
                      )"
                      :key="'old-' + key"
                    >
                      <div
                        v-html="
                          formatUpdatedValues(
                            key,
                            oldValue,
                            row.properties.changes.new[key],
                            row.log_name
                          )
                        "
                      ></div>
                    </li>
                  </ul>
                </b-list-group-item>
              </b-list-group>
            </b-collapse>
          </app-timeline-item>

          <!-- Display other events if needed -->
          <app-timeline-item
            :key="row.id + '-other'"
            v-if="row.event !== 'created' && row.event !== 'updated'"
            variant="info"
          >
            <div
              class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0"
            >
              <h6
                v-html="formatMsg(row.log_name, row.properties, row.event)"
              ></h6>
              <small class="text-muted"
                >{{ row.event | titleCase }}:
                {{ formatDate(row.created_at) }}</small
              >
            </div>
          </app-timeline-item>
        </template>
      </app-timeline>
      <div v-if="rows.length === 0" class="text-center py-5">
        <p>No activity logs found for this site list.</p>
      </div>
    </b-card>
  </span>
</template>

<script>
import {
  BButton,
  BCollapse,
  VBToggle,
  BListGroup,
  BListGroupItem,
  BCard,
  BRow,
  BCol,
} from "bootstrap-vue";
import AppTimeline from "@core/components/app-timeline/AppTimeline.vue";
import AppTimelineItem from "@core/components/app-timeline/AppTimelineItem.vue";
import Ripple from "vue-ripple-directive";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment";

export default {
  components: {
    AppTimeline,
    AppTimelineItem,
    BCard,
    BButton,
    BCollapse,
    BListGroup,
    BListGroupItem,
    BRow,
    BCol,
  },
  directives: { "b-toggle": VBToggle, Ripple },
  filters: {
    titleCase(value) {
      if (!value) return "";
      return value.replace(
        /\w\S*/g,
        (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      );
    },
  },
  data() {
    return {
      rows: [],
      filteredCurrencyOptions: [],
      siteListId: null,
    };
  },
  async created() {
    this.siteListId = this.$route.params.id;
    try {
      await Promise.all([this.getAllCurrencies()]);
      await this.loadItems();
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message || "Failed to load data",
        },
      });
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format("MMM DD, YYYY hh:mm A");
    },
    async getAllCurrencies() {
      try {
        const response = await this.$api.get("api/currencies/all");
        this.filteredCurrencyOptions = (response?.data?.data || []).map(
          (item) => ({
            name: item.name,
            currency: item.currency,
            id: item.id,
          })
        );
      } catch (error) {
        console.error("Failed to fetch currencies", error);
      }
    },
    async loadItems() {
      try {
        const response = await this.$api.get(
          `api/outreach/site-list/activity-log/${this.siteListId}`
        );
        this.rows = response.data || [];
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message || "Failed to load logs",
          },
        });
      }
    },
    formatMsg(log_name, properties, event) {
      const causer = properties?.causer_name || "Admin";
      if (log_name === "sitelist" || log_name === "default") {
        if (event === "created") {
          return `<span class="font-weight-bolder text-primary">${causer}</span> created the site list: <span class="font-weight-bolder text-primary">${
            properties?.attributes?.website || "N/A"
          }</span>`;
        } else if (event === "updated") {
          return `<span class="font-weight-bolder text-warning">${causer}</span> updated the site list: <span class="font-weight-bolder text-warning">${
            properties?.attributes?.website || "N/A"
          }</span>`;
        }
      }
      return `<span class="font-weight-bolder text-info">${causer}</span> performed <span class="text-info">${event}</span> on <span class="font-weight-bolder text-info">${log_name}</span>`;
    },
    formatUpdatedValues(key, oldValue, newValue, log_name) {
      const fieldLabels = {
        website: "Website",
        niche_id: "Niche",
        seller_id: "Seller",
        traffic: "Traffic",
        traffic_value: "Traffic Value",
        spam_score: "Spam Score",
        domain_authority: "Domain Authority",
        referal_domain: "Referral Domain",
        remarks: "Remarks",
        status: "Status",
        post_price: "Post Price",
      };

      const label = fieldLabels[key] || key;

      if (key === "traffic_value_currency" || key === "post_price_currency") {
        const oldCurrency =
          this.filteredCurrencyOptions.find((item) => item.id === oldValue)
            ?.currency || oldValue;
        const newCurrency =
          this.filteredCurrencyOptions.find((item) => item.id === newValue)
            ?.currency || newValue;
        return `<strong>${label}:</strong> ${oldCurrency} &rarr; <strong>${newCurrency}</strong>`;
      }

      if (key === "status") {
        const statusOptions = [
          { id: 0, name: "Pending" },
          { id: 1, name: "Approved" },
          { id: 2, name: "Rejected" },
          { id: 3, name: "Soft Reject" },
        ];
        const oldStatus =
          statusOptions.find((option) => option.id === oldValue)?.name ||
          oldValue;
        const newStatus =
          statusOptions.find((option) => option.id === newValue)?.name ||
          newValue;
        return `<strong>${label}:</strong> ${oldStatus} &rarr; <strong>${newStatus}</strong>`;
      }

      return `<strong>${label}:</strong> ${oldValue} &rarr; <strong>${newValue}</strong>`;
    },
    getFilteredChanges(properties) {
      if (!properties) return {};

      let newVals =
        properties.attributes ||
        (properties.changes ? properties.changes.new : null) ||
        properties.new ||
        properties;

      let oldVals =
        properties.old ||
        (properties.changes ? properties.changes.old : null) ||
        {};

      // If newVals is the properties object itself, clean up nested keys
      if (newVals === properties) {
        newVals = { ...properties };
        delete newVals.attributes;
        delete newVals.old;
        delete newVals.changes;
        delete newVals.causer;
      }

      const filtered = {};
      const ignoredKeys = ["updated_at", "created_at", "id"];

      Object.keys(newVals).forEach((key) => {
        if (
          !ignoredKeys.includes(key) &&
          JSON.stringify(oldVals[key]) !== JSON.stringify(newVals[key])
        ) {
          filtered[key] = oldVals[key];
        }
      });
      return filtered;
    },
  },
};
</script>
